import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar'
import { NavGroup } from '@/components/layout/nav-group'
import { LogOut } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useSidebarData } from './data/use-sidebar-data'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const auth = useAuthStore()
  const { sidebarData } = useSidebarData()
  const { i18n, t } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const side: 'left' | 'right' = isRTL ? 'right' : 'left'

  const [, forceUpdate] = useState({})

  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({})
    }

    i18n.on('languageChanged', handleLanguageChange)

    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [i18n])

  const user = auth.auth.user
  const userInitials = user?.name
    ? user.name.split(' ').map(n => n.charAt(0)).slice(0, 2).join('')
    : 'U'

  const handleLogout = () => {
    auth.auth.reset()
    window.location.href = '/auth/login'
  }

  return (
    <Sidebar
      side={side}
      collapsible='icon'
      variant='sidebar'
      {...props}
      className={cn(
        "bg-primary text-primary-foreground shadow-xl border-e border-blue-800 dark:border-blue-900",
        props.className
      )}
    >
      {/* Logo Header */}
      <SidebarHeader className="h-16 flex items-center justify-center border-b border-blue-800 dark:border-blue-900 bg-opacity-10 px-6 py-0">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              className="hover:bg-white/10 transition-all duration-300 group data-[state=open]:bg-transparent"
            >
              <a href="/" className="flex items-center gap-2">
                <span className="material-icons-outlined text-secondary text-3xl"></span>
                <div className="flex flex-col gap-0.5 leading-none group-data-[collapsible=icon]:hidden">
                  <span className="text-xl font-bold tracking-wider">
                    Avocat<span className="text-secondary">Pro</span>
                  </span>
                </div>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* User Profile Section */}
      <div className="p-6 border-b border-blue-800 dark:border-blue-900 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-4">
        <div className="flex items-center gap-3 group-data-[collapsible=icon]:justify-center">
          <Avatar className="h-10 w-10 border-2 border-white/20">
            <AvatarImage src={undefined} alt={user?.name || 'User'} />
            <AvatarFallback className="bg-secondary text-secondary-foreground font-semibold">
              {userInitials}
            </AvatarFallback>
          </Avatar>
          <div className="group-data-[collapsible=icon]:hidden">
            <p className="text-sm font-semibold">
              {user?.cabinet || 'AvocatPro Cabinet'}
            </p>
            <p className="text-xs text-blue-200">
              {user?.name}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Content */}
      <SidebarContent className="py-4 px-3">
        {sidebarData.navGroups.map((props) => (
          <NavGroup key={props.title} {...props} />
        ))}
      </SidebarContent>

      {/* Footer with Logout */}
      <SidebarFooter className="border-t border-sidebar-border/30 p-4">
        <Button
          variant="ghost"
          onClick={handleLogout}
          className={cn(
            "w-full flex items-center justify-center gap-2",
            "bg-red-500/10 hover:bg-red-500 hover:text-white",
            "text-red-200 transition-colors text-sm rounded-lg",
            "group-data-[collapsible=icon]:p-2 group-data-[collapsible=icon]:w-auto"
          )}
        >
          <LogOut className="h-4 w-4" />
          <span className="group-data-[collapsible=icon]:hidden">{t('logout')}</span>
        </Button>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}